import React, { useEffect, useState, useCallback } from "react";
import { MdStar, MdStarBorder, MdThumbUp, MdThumbDown } from "react-icons/md";
import Card from "components/card";
import { useAuth } from "contexts/AuthContext";
import { getRatings } from "services/reviewsService";

function Stars({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <span key={s} className={s <= rating ? "text-amber-400" : "text-gray-200"}>
          {s <= rating ? <MdStar className="h-4 w-4" /> : <MdStarBorder className="h-4 w-4" />}
        </span>
      ))}
    </div>
  );
}

function RatingBar({ label, count, total }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className="flex items-center gap-2 text-sm">
      <span className="w-5 text-right text-gray-500 font-medium">{label}</span>
      <MdStar className="h-3.5 w-3.5 text-amber-400 flex-shrink-0" />
      <div className="flex-1 h-2 rounded-full bg-gray-100 dark:bg-navy-700 overflow-hidden">
        <div
          className="h-full rounded-full bg-amber-400"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="w-7 text-xs text-gray-400">{count}</span>
    </div>
  );
}

function ReviewCard({ review }) {
  const rating = review.rating || review.rate || 0;
  const isPositive = rating >= 4;
  return (
    <Card extra="p-4 mb-3">
      <div className="flex items-start justify-between mb-2">
        <div>
          <Stars rating={rating} />
          <p className="text-xs text-gray-400 mt-1">
            {review.created_at ? new Date(review.created_at).toLocaleDateString("fr-FR") : "—"}
          </p>
        </div>
        <span className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${
          isPositive ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"
        }`}>
          {isPositive
            ? <><MdThumbUp className="h-3.5 w-3.5" /> Positif</>
            : <><MdThumbDown className="h-3.5 w-3.5" /> Négatif</>}
        </span>
      </div>
      {review.comment && (
        <p className="text-sm text-gray-600 dark:text-gray-300">{review.comment}</p>
      )}
    </Card>
  );
}

const TABS = [
  { key: "all", label: "Tous" },
  { key: "positive", label: "Positifs (≥4★)" },
  { key: "negative", label: "Négatifs (<4★)" },
];

export default function Reviews() {
  const { restaurantId } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average: 0, total: 0, distribution: {} });
  const [activeTab, setActiveTab] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const LIMIT = 15;

  const load = useCallback(async (reset = false) => {
    if (!restaurantId) return;
    const p = reset ? 1 : page;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const data = await getRatings(restaurantId, { limit: LIMIT, page: p });
      const list = Array.isArray(data) ? data : data.data || [];
      const meta = data.meta || data.summary || {};

      if (reset) {
        setReviews(list);
        setPage(2);
      } else {
        setReviews((prev) => [...prev, ...list]);
        setPage((prev) => prev + 1);
      }
      setHasMore(list.length === LIMIT);

      if (meta.average || meta.total) {
        const dist = {};
        [1, 2, 3, 4, 5].forEach((s) => {
          dist[s] = meta[`star_${s}`] || meta.distribution?.[s] || 0;
        });
        setSummary({
          average: meta.average || 0,
          total: meta.total || list.length,
          distribution: dist,
        });
      }
    } catch {}
    finally { if (reset) setLoading(false); else setLoadingMore(false); }
  }, [restaurantId, page]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(true); }, [restaurantId]);

  const filtered = reviews.filter((r) => {
    const rating = r.rating || r.rate || 0;
    if (activeTab === "positive") return rating >= 4;
    if (activeTab === "negative") return rating < 4;
    return true;
  });

  return (
    <div className="mt-3">
      <h2 className="text-2xl font-bold text-navy-700 dark:text-white flex items-center gap-2 mb-5">
        <MdStar className="h-7 w-7 text-amber-400" />
        Avis clients
      </h2>

      {/* Summary card */}
      <Card extra="p-5 mb-5">
        <div className="flex gap-6 flex-wrap">
          {/* Score */}
          <div className="flex flex-col items-center justify-center min-w-[80px]">
            <span className="text-5xl font-extrabold text-navy-700 dark:text-white">
              {Number(summary.average || 0).toFixed(1)}
            </span>
            <Stars rating={Math.round(summary.average)} />
            <span className="text-xs text-gray-400 mt-1">{summary.total} avis</span>
          </div>
          {/* Distribution */}
          <div className="flex-1 min-w-[160px] space-y-1.5">
            {[5, 4, 3, 2, 1].map((s) => (
              <RatingBar
                key={s}
                label={s}
                count={summary.distribution[s] || 0}
                total={summary.total || 1}
              />
            ))}
          </div>
        </div>
      </Card>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 bg-gray-50 dark:bg-navy-700 rounded-xl p-1 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            className={`rounded-xl px-4 py-2 text-sm font-semibold transition-colors ${
              activeTab === tab.key
                ? "bg-white dark:bg-navy-800 text-navy-700 dark:text-white shadow-sm"
                : "text-gray-500 dark:text-gray-400"
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : filtered.length === 0 ? (
        <Card extra="p-8 text-center">
          <p className="text-gray-400">Aucun avis dans cette catégorie</p>
        </Card>
      ) : (
        <>
          {filtered.map((r, i) => <ReviewCard key={r.id || i} review={r} />)}
          {hasMore && (
            <div className="text-center mt-4">
              <button
                className="rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                onClick={() => load(false)}
                disabled={loadingMore}
              >
                {loadingMore ? "Chargement..." : "Charger plus"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
